import { Timestamp } from 'firebase/firestore';
import { useCallback, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { FirebaseReducer, useFirebase } from 'react-redux-firebase';
import { actionTypes } from 'redux-firestore';
import { Customer, Customers } from '../common/Customer';
import { useCountryMarketState } from '../controllers/CountryMarketController/CountryMarketState';
import { useBriefingState } from '../features/Briefing/BriefingState';
import { useCustomerState } from '../features/Briefing/CustomerState';
import { useIngredientsState } from '../features/Briefing/IngredientsState';
import { RootState, User } from '../shared/store';
import { TrackingSchema } from '../types';
import { useCustomers, useUser } from './queries';
import { useAppRoutes } from './useAppRoutes';
import { useGTM } from './useGTM';

/**
 * The time (in *milliseconds*) the user remains logged in
 * after some activity
 *
 * currently 4 hours
 */
//                                ms   * s  * m  * h
const ACTIVITY_EXPIRATION_AFTER = 1000 * 60 * 60 * 4;

export type Auth = [
  {
    auth: FirebaseReducer.AuthState;
    user?: User;
    customerUser: User | null;
    displayUser?: User;
    customer: Customer | null;
    customerId: string | null;
    customers?: Customers;
  },
  { signOut: () => void },
];

const loginExpiresInstances: Array<number | NodeJS.Timeout> = [];

const resetLoginExpiresInstances = () =>
  loginExpiresInstances.forEach(() =>
    clearTimeout(loginExpiresInstances.shift()),
  );

export const useAuth: () => Auth = () => {
  const firebase = useFirebase();
  const dispatch = useDispatch();
  const { briefingRoute } = useAppRoutes();

  const user = useUser();
  const auth = useSelector((state: RootState) => state.firebase.auth);

  const [, { resetAll: resetIngredientsState }] = useIngredientsState();
  const [, { resetAll: resetBriefingState }] = useBriefingState();
  const [, { resetAll: resetCountryMarketState }] = useCountryMarketState();
  const [
    { customerUser, customerId, customer },
    { resetAll: resetCustomerState, setCustomer },
  ] = useCustomerState();

  const displayUser = customerUser || user;

  const customers = useCustomers(displayUser);

  useEffect(() => {
    dispatch(setCustomer(customers?.[customerId || ''] ?? null));
  }, [customerId, customers, dispatch, setCustomer]);

  const { updateDataLayer } = useGTM();

  const signOut = useCallback(() => {
    if (user) {
      const userTrackingData: Record<string, unknown> = {
        userGUID: user.GUID,
        identStatus: user.azelis_employee ? 'employee' : 'customer',
        customerGUID: customer?.GUID,
        customerName: customer?.customer_name,
        access: true,
      };

      if (user.customers.length > 1 && customers) {
        const customerArr = Object.values(customers);

        // a universal customer
        userTrackingData.assignedCustomerGUIDs = customerArr.map(
          ({ GUID }) => GUID,
        );

        userTrackingData.assignedCustomerNames = customerArr.map(
          ({ customer_name }) => customer_name,
        );
      }
      updateDataLayer({
        event: 'User Logout',
        schema: TrackingSchema.user,
        data: {
          userGUID: user.GUID,
          identStatus: user.azelis_employee ? 'employee' : 'customer',
          customerGUID: customer?.GUID,
          customerName: customer?.customer_name,
          access: true,
        },
      });
    }

    firebase
      .auth()
      .signOut()
      .then(() => {
        batch(() => {
          dispatch({
            type: actionTypes.CLEAR_DATA,
            preserve: { data: ['features'] },
          });
          dispatch(resetCountryMarketState());
          dispatch(resetCustomerState());
          dispatch(resetIngredientsState());
          dispatch(resetBriefingState());
        });
      })
      .catch(console.error);
  }, [
    dispatch,
    firebase,
    resetBriefingState,
    resetCountryMarketState,
    resetCustomerState,
    resetIngredientsState,
    user,
    customer,
    customers,
    updateDataLayer,
  ]);

  /**
   * This Effect handles user logout after the Login expired
   */
  useEffect(() => {
    if (!user) return;
    resetLoginExpiresInstances();

    const isInBriefingPath = window.location.pathname.includes(briefingRoute);

    if (isInBriefingPath) return;

    const loginExpires =
      (user.loginExpires as Timestamp)?.toDate() || new Date();

    const timeRemaining = Math.max(
      loginExpires.valueOf() - new Date().valueOf(),
      ACTIVITY_EXPIRATION_AFTER,
    );

    const timeoutInstance = setTimeout(signOut, timeRemaining);

    loginExpiresInstances.push(timeoutInstance);

    return resetLoginExpiresInstances;
  }, [briefingRoute, signOut, user]);

  return [
    { auth, user, customerUser, displayUser, customer, customerId, customers },
    { signOut },
  ];
};
